import { useEffect, useState } from "react";

import { type PlatformData, type QrScanResult, type TelegramData, type UsePlatformFeature } from "./types";

const platformModulePromise = (() => {
  // eslint-disable-next-line dot-notation
  const platform = import.meta.env["VITE_PLATFORM"];

  switch (platform) {
    case "tma":
      return import("./tma");
    case "android":
      return import("./android");
    case "ios":
      return import("./android");
    case "web":
    default:
      return import("./web");
  }
})();

let loadedModule: { usePlatformFeature: () => UsePlatformFeature } | null = null;

platformModulePromise.then((module) => {
  loadedModule = module;
});

export const usePlatformFeature = (): UsePlatformFeature => {
  const [implementation, setImplementation] = useState<UsePlatformFeature | null>(
    loadedModule ? loadedModule.usePlatformFeature() : null,
  );

  useEffect(() => {
    if (loadedModule) {
      setImplementation(loadedModule.usePlatformFeature());
      return;
    }

    let isMounted = true;
    platformModulePromise.then((module) => {
      if (isMounted) {
        setImplementation(module.usePlatformFeature());
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  if (implementation) {
    return implementation;
  }

  return {
    usePlatformData: (): PlatformData => ({
      platform: "loading" as "tma" | "web" | "android" | "ios",
      user: { id: "loading" },
    }),
    openLink: async (): Promise<boolean> => {
      console.warn("Platform module is still loading");
      return false;
    },
    showNotification: (message: string): void => {
      console.warn("Platform module is still loading:", message);
    },
    useTelegramData: (): TelegramData => ({
      platform: "loading",
      version: "1.0",
      initDataRaw: "",
      initData: {
        user: {
          id: 0,
          username: "loading",
          first_name: "Loading",
          last_name: "User",
        },
        authDate: Math.floor(Date.now() / 1000),
        hash: "",
      },
    }),
    scanQrCode: async (): Promise<QrScanResult> => {
      console.warn("Platform module is still loading, QR scanner not available");
      return {
        text: "",
        cancelled: true,
      };
    },
    hapticFeedback: async (strength: "light" | "medium" | "heavy"): Promise<void> => {
      console.warn(
        `Haptic feedback with strength "${strength}" is not available as the platform module is still loading`,
      );
    },
  };
};
